import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { format, addDays, startOfToday, isBefore, isToday, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { toast } from "sonner";
import { Scissors, Clock, ChevronLeft, ChevronRight, Check, Zap, Calendar as CalendarIcon, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getServicios, getPeluqueros, getDisponibilidad, createCita, getConfiguracion, getProximosHuecos, getDiasDisponibles } from "@/lib/api";

import DisponibilidadSemanal from "@/components/DisponibilidadSemanal";

const STEPS = ["servicio", "fecha", "hora", "datos"];

export default function BookingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);
  
  // Data
  const [servicios, setServicios] = useState([]);
  const [peluqueros, setPeluqueros] = useState([]);
  const [slots, setSlots] = useState([]);
  const [proximosHuecos, setProximosHuecos] = useState([]);
  const [loadingProximos, setLoadingProximos] = useState(false);
  const [diasDisponibles, setDiasDisponibles] = useState(new Set());
  const [diasCargados, setDiasCargados] = useState(false);
  
  // Selection
  const [selectedServicio, setSelectedServicio] = useState(null);
  const [selectedFecha, setSelectedFecha] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedPeluquero, setSelectedPeluquero] = useState(null);
  const [bookingMode, setBookingMode] = useState("rapido"); // "rapido" o "calendario"
  
  // Client data
  const [nombre, setNombre] = useState("");
  const [telefono, setTelefono] = useState("");
  const stepRef = useRef(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  // Filter services to only show those with at least one active barber
  const serviciosDisponibles = servicios.filter(servicio => {
    return peluqueros.some(p => {
      const ids = p.servicios_ids || [];
      return ids.length === 0 || ids.includes(servicio.id);
    });
  });

  useEffect(() => {
    if (selectedServicio && bookingMode === "rapido") {
      loadProximosHuecos();
    }
  }, [selectedServicio, bookingMode]);

  useEffect(() => {
    if (selectedServicio && selectedFecha && bookingMode === "calendario") {
      loadDisponibilidad();
    }
  }, [selectedServicio, selectedFecha, selectedPeluquero, bookingMode]);

  useEffect(() => {
    if (selectedServicio && bookingMode === "calendario") {
      loadDiasDisponibles();
    }
  }, [selectedServicio, selectedPeluquero, bookingMode]);

  const loadInitialData = async () => {
    try {
      const [serviciosRes, peluquerosRes, configRes] = await Promise.all([
        getServicios(true),
        getPeluqueros(true),
        getConfiguracion()
      ]);
      setServicios(serviciosRes.data);
      setPeluqueros(peluquerosRes.data);
      setConfig(configRes.data);
    } catch (error) {
      toast.error("Error al cargar los datos");
    }
  };

  const loadProximosHuecos = async () => {
    if (!selectedServicio) return;
    setLoadingProximos(true);
    try {
      const res = await getProximosHuecos(selectedServicio.id, 10);
      setProximosHuecos(res.data.huecos || []);
    } catch (error) {
      console.error("Error loading proximos huecos:", error);
      setProximosHuecos([]);
    } finally {
      setLoadingProximos(false);
    }
  };

  const loadDiasDisponibles = async () => {
    if (!selectedServicio) return;
    setDiasCargados(false);
    try {
      const res = await getDiasDisponibles(selectedServicio.id, selectedPeluquero?.id);
      setDiasDisponibles(new Set(res.data.dias_disponibles || []));
    } catch (error) {
      setDiasDisponibles(new Set());
    } finally {
      setDiasCargados(true);
    }
  };

  const loadDisponibilidad = async () => {
    setLoading(true);
    try {
      const fecha = format(selectedFecha, "yyyy-MM-dd");
      const res = await getDisponibilidad(
        selectedServicio.id,
        fecha,
        selectedPeluquero?.id
      );
      setSlots(res.data.slots || []);
    } catch (error) {
      toast.error("Error al cargar disponibilidad");
      setSlots([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectProximoHueco = (hueco) => {
    setSelectedFecha(parseISO(hueco.fecha));
    setSelectedSlot({
      hora: hueco.hora,
      peluquero_id: hueco.peluquero_id,
      peluquero_nombre: hueco.peluquero_nombre,
      peluquero_color: hueco.peluquero_color
    });
    setStep(3); // Ir directamente a datos
  };

  const handleNext = () => {
    if (step < STEPS.length - 1) {
      setStep(step + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleSubmit = async () => {
    if (!nombre.trim() || !telefono.trim()) {
      toast.error("Por favor, completa todos los campos");
      return;
    }

    setLoading(true);
    try {
      const res = await createCita({
        cliente_nombre: nombre.trim(),
        cliente_telefono: telefono.trim(),
        servicio_id: selectedServicio.id,
        peluquero_id: selectedSlot.peluquero_id,
        fecha: format(selectedFecha, "yyyy-MM-dd"),
        hora_inicio: selectedSlot.hora
      });
      navigate(`/confirmacion/${res.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear la cita");
    } finally {
      setLoading(false);
    }
  };

  const today = startOfToday();
  const maxDate = addDays(today, 21);

  // Check if date is a holiday
  const isHoliday = (date) => {
    if (!config?.festivos) return false;
    const dateStr = format(date, "yyyy-MM-dd");
    return config.festivos.some(f => f.fecha === dateStr);
  };

  // Check if any relevant barber is structurally available on this date
  // (works that weekday, not on día libre, and offers the selected service).
  // Respects the selected barber; if "Cualquiera", any barber counts.
  const dayHasBarberAvailability = (date) => {
    const jsDay = date.getDay(); // 0=Sunday...6=Saturday
    const diaSemana = (jsDay + 6) % 7; // 0=Lunes...6=Domingo
    const fechaStr = format(date, "yyyy-MM-dd");
    const relevantes = selectedPeluquero
      ? peluqueros.filter(p => p.id === selectedPeluquero.id)
      : peluqueros;
    return relevantes.some(p => {
      const ids = p.servicios_ids || [];
      if (ids.length > 0 && selectedServicio && !ids.includes(selectedServicio.id)) return false;
      if ((p.dias_libres || []).some(dl => dl.fecha === fechaStr)) return false;
      const horario = (p.horarios || []).find(h => h.dia === diaSemana && h.activo);
      return !!horario;
    });
  };

  // Filter slots for today - remove past hours
  const getFilteredSlots = () => {
    if (!selectedFecha || !isToday(selectedFecha)) {
      return slots;
    }
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    return slots.filter(slot => {
      const [h, m] = slot.hora.split(":").map(Number);
      const slotMinutes = h * 60 + m;
      return slotMinutes > currentMinutes;
    });
  };

  // Group slots by time for display
  const filteredSlots = getFilteredSlots();
  const groupedSlots = filteredSlots.reduce((acc, slot) => {
    if (!acc[slot.hora]) {
      acc[slot.hora] = [];
    }
    acc[slot.hora].push(slot);
    return acc;
  }, {});

  const formatProximoHueco = (hueco) => {
    const fecha = parseISO(hueco.fecha);
    if (isToday(fecha)) {
      return `Hoy a las ${hueco.hora}`;
    }
    const tomorrow = addDays(today, 1);
    if (format(fecha, "yyyy-MM-dd") === format(tomorrow, "yyyy-MM-dd")) {
      return `Mañana a las ${hueco.hora}`;
    }
    return `${format(fecha, "EEEE d", { locale: es })} a las ${hueco.hora}`;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-primary text-primary-foreground py-6 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              asChild
              className="text-white hover:bg-white/10"
            >
              <Link to="/">
                <ArrowLeft className="h-5 w-5" />
              </Link>
            </Button>
            <div className="flex-1 text-center pr-10">
              <h1 className="text-2xl md:text-3xl font-heading" data-testid="booking-title">
                Reservar cita
              </h1>
              <p className="text-sm opacity-90">{config?.nombre_negocio || "Barbería Clásica"}</p>
            </div>
          </div>
        </div>
      </header>

      {/* Progress */}
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-8">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-medium transition-colors ${
                  i <= step
                    ? "bg-accent text-accent-foreground"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                {i < step ? <Check className="h-5 w-5" /> : i + 1}
              </div>
              {i < STEPS.length - 1 && (
                <div
                  className={`w-12 md:w-20 h-1 mx-1 ${
                    i < step ? "bg-accent" : "bg-muted"
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <div className="min-h-[400px]">
          {/* Step 1: Servicio */}
          {step === 0 && (
            <div className="space-y-4" data-testid="step-servicio">
              <h2 className="text-2xl font-heading mb-6">¿Qué servicio necesitas?</h2>
              <div className="grid gap-4">
                {serviciosDisponibles.length === 0 ? (
                  <div className="text-center py-8">
                    <Scissors className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-lg text-muted-foreground">
                      No hay servicios disponibles en este momento
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      Contacta con nosotros para más información
                    </p>
                  </div>
                ) : serviciosDisponibles.map((servicio) => (
                  <Card
                    key={servicio.id}
                    data-testid={`servicio-${servicio.id}`}
                    className={`cursor-pointer transition-all hover:border-accent ${
                      selectedServicio?.id === servicio.id
                        ? "ring-2 ring-accent border-accent bg-accent/5"
                        : ""
                    }`}
                    onClick={() => {
                      setSelectedServicio(servicio);
                      setTimeout(() => {
                        setStep(1);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }, 300);
                    }}
                  >
                    <CardContent className="p-6 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                          <Scissors className="h-6 w-6 text-accent" />
                        </div>
                        <div>
                          <h3 className="text-xl font-medium">{servicio.nombre}</h3>
                          {servicio.descripcion && (
                            <p className="text-sm text-muted-foreground mt-0.5">{servicio.descripcion}</p>
                          )}
                          <div className="flex items-center gap-4 text-muted-foreground mt-1">
                            <span className="flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              {servicio.duracion_minutos} min
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="text-2xl font-heading text-accent">
                        {servicio.precio}€
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Fecha - con modo rápido */}
          {step === 1 && (
            <div className="space-y-4" data-testid="step-fecha">
              <h2 className="text-2xl font-heading mb-2">¿Cuándo quieres venir?</h2>
              
              <Tabs value={bookingMode} onValueChange={setBookingMode} className="w-full">
                <TabsList className="grid w-full grid-cols-3 mb-6">
                  <TabsTrigger value="rapido" className="gap-2">
                    <Zap className="h-4 w-4" />
                    Rapida
                  </TabsTrigger>
                  <TabsTrigger value="calendario" className="gap-2">
                    <CalendarIcon className="h-4 w-4" />
                    Elegir fecha
                  </TabsTrigger>
                  <TabsTrigger value="disponibilidad" className="gap-2">
                    <Clock className="h-4 w-4" />
                    Ver huecos
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="rapido">
                  <div className="space-y-4">
                    <p className="text-muted-foreground">
                      Próximos huecos disponibles para <strong>{selectedServicio?.nombre}</strong>:
                    </p>
                    
                    {loadingProximos ? (
                      <div className="flex justify-center py-8">
                        <div className="spinner w-8 h-8" />
                      </div>
                    ) : proximosHuecos.length === 0 ? (
                      <div className="text-center py-8">
                        <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                        <p className="text-muted-foreground">No hay huecos disponibles próximamente</p>
                        <Button
                          variant="outline"
                          className="mt-4"
                          onClick={() => setBookingMode("calendario")}
                        >
                          Buscar en el calendario
                        </Button>
                      </div>
                    ) : (
                      <div className="grid gap-3">
                        {proximosHuecos.map((hueco, idx) => (
                          <Card
                            key={`${hueco.fecha}-${hueco.hora}-${hueco.peluquero_id}-${idx}`}
                            className="cursor-pointer transition-all hover:border-accent hover:bg-accent/5"
                            onClick={() => handleSelectProximoHueco(hueco)}
                            data-testid={`hueco-rapido-${idx}`}
                          >
                            <CardContent className="p-4 flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div
                                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                                  style={{ backgroundColor: hueco.peluquero_color || "#3A5A40" }}
                                >
                                  {hueco.peluquero_nombre?.charAt(0)}
                                </div>
                                <div>
                                  <p className="font-medium text-lg">{formatProximoHueco(hueco)}</p>
                                  <p className="text-sm text-muted-foreground">
                                    con {hueco.peluquero_nombre}
                                  </p>
                                </div>
                              </div>
                              <ChevronRight className="h-5 w-5 text-muted-foreground" />
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="calendario">
                  {/* Peluquero filter */}
                  <div className="mb-6">
                    <Label className="text-base mb-3 block">Filtrar por peluquero (opcional)</Label>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant={selectedPeluquero === null ? "default" : "outline"}
                        className="h-10"
                        onClick={() => setSelectedPeluquero(null)}
                      >
                        Cualquiera
                      </Button>
                      {peluqueros.map((p) => (
                        <Button
                          key={p.id}
                          variant={selectedPeluquero?.id === p.id ? "default" : "outline"}
                          className="h-10"
                          onClick={() => setSelectedPeluquero(p)}
                        >
                          {p.nombre}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-center">
                    <Calendar
                      mode="single"
                      selected={selectedFecha}
                      onSelect={(date) => {
                        setSelectedFecha(date);
                        if (date) {
                          setTimeout(() => {
                            setStep(2);
                            window.scrollTo({ top: 0, behavior: "smooth" });
                          }, 300);
                        }
                      }}
                      disabled={(date) => {
                        if (isBefore(date, today) || date > maxDate) return true;
                        if (diasCargados) return !diasDisponibles.has(format(date, "yyyy-MM-dd"));
                        return isHoliday(date) || !dayHasBarberAvailability(date);
                      }}
                      locale={es}
                      className="rounded-md border p-4"
                      data-testid="calendar"
                    />
                  </div>
                </TabsContent>

                <TabsContent value="disponibilidad">
                  <DisponibilidadSemanal
                    servicioId={selectedServicio?.id}
                    peluqueros={peluqueros}
                    onSelectHueco={handleSelectProximoHueco}
                  />
                </TabsContent>
              </Tabs>
            </div>
          )}

          {/* Step 3: Hora (solo si viene del calendario) */}
          {step === 2 && (
            <div className="space-y-4" data-testid="step-hora">
              <h2 className="text-2xl font-heading mb-2">¿A qué hora?</h2>
              <p className="text-muted-foreground mb-6">
                {format(selectedFecha, "EEEE d 'de' MMMM", { locale: es })}
                {isToday(selectedFecha) && (
                  <Badge variant="secondary" className="ml-2">Hoy</Badge>
                )}
              </p>
              
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="spinner w-10 h-10" />
                </div>
              ) : Object.keys(groupedSlots).length === 0 ? (
                <div className="text-center py-12">
                  <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-lg text-muted-foreground">
                    {isToday(selectedFecha) 
                      ? "No quedan huecos disponibles para hoy"
                      : "No hay huecos disponibles para este día"
                    }
                  </p>
                  <Button
                    variant="outline"
                    className="mt-4 h-12"
                    onClick={handleBack}
                  >
                    Elegir otro día
                  </Button>
                </div>
              ) : (
                <div className="grid gap-3">
                  {Object.entries(groupedSlots).map(([hora, slotsHora]) => (
                    <div key={hora} className="space-y-2">
                      <div className="text-sm font-medium text-muted-foreground">{hora}</div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {slotsHora.map((slot, idx) => (
                          <Card
                            key={`${slot.hora}-${slot.peluquero_id}-${idx}`}
                            data-testid={`slot-${slot.hora}-${slot.peluquero_id}`}
                            className={`cursor-pointer transition-all hover:border-accent ${
                              selectedSlot?.hora === slot.hora &&
                              selectedSlot?.peluquero_id === slot.peluquero_id
                                ? "ring-2 ring-accent border-accent bg-accent/5"
                                : ""
                            }`}
                            onClick={() => {
                              setSelectedSlot(slot);
                              setTimeout(() => {
                                setStep(3);
                                window.scrollTo({ top: 0, behavior: "smooth" });
                              }, 300);
                            }}
                          >
                            <CardContent className="p-4 flex items-center gap-3">
                              <div
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: slot.peluquero_color }}
                              />
                              <div>
                                <div className="font-medium">{slot.peluquero_nombre}</div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 4: Datos */}
          {step === 3 && (
            <div className="space-y-6" data-testid="step-datos">
              <h2 className="text-2xl font-heading mb-6">Tus datos</h2>
              
              {/* Summary */}
              <Card className="bg-muted/50">
                <CardContent className="p-6 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Servicio:</span>
                    <span className="font-medium">{selectedServicio?.nombre}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Fecha:</span>
                    <span className="font-medium">
                      {format(selectedFecha, "d 'de' MMMM", { locale: es })}
                      {isToday(selectedFecha) && " (Hoy)"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Hora:</span>
                    <span className="font-medium">{selectedSlot?.hora}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Peluquero:</span>
                    <span className="font-medium">{selectedSlot?.peluquero_nombre}</span>
                  </div>
                  <div className="border-t pt-3 flex justify-between">
                    <span className="text-lg">Total:</span>
                    <span className="text-2xl font-heading text-accent">
                      {selectedServicio?.precio}€
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Form */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="nombre" className="text-base mb-2 block">
                    Tu nombre completo *
                  </Label>
                  <Input
                    id="nombre"
                    data-testid="input-nombre"
                    placeholder="Ej: Juan García López"
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                    className="h-14 text-lg"
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    Introduce tu nombre y apellidos completos
                  </p>
                </div>
                <div>
                  <Label htmlFor="telefono" className="text-base mb-2 block">
                    Tu teléfono *
                  </Label>
                  <Input
                    id="telefono"
                    data-testid="input-telefono"
                    type="tel"
                    placeholder="Ej: 612345678"
                    value={telefono}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "").slice(0, 9);
                      setTelefono(val);
                    }}
                    className="h-14 text-lg"
                    maxLength={9}
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    Sin +34, solo 9 digitos
                  </p>
                  {telefono && telefono.length !== 9 && (
                    <p className="text-sm text-destructive mt-1" data-testid="phone-error">
                      El telefono debe tener exactamente 9 cifras ({telefono.length}/9)
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation - Solo botón Atrás y Confirmar */}
        <div className="flex justify-between mt-6 pt-4 border-t pb-20">
          <Button
            variant="outline"
            className="h-14 px-6"
            onClick={handleBack}
            disabled={step === 0}
            data-testid="btn-back"
          >
            <ChevronLeft className="h-5 w-5 mr-2" />
            Atrás
          </Button>
          
          {step === STEPS.length - 1 && (
            <Button
              className="h-14 px-8 bg-accent hover:bg-accent/90"
              onClick={handleSubmit}
              disabled={loading || !nombre.trim() || telefono.length !== 9}
              data-testid="btn-confirmar"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="spinner w-5 h-5 border-white" />
                  Confirmando...
                </span>
              ) : (
                <>
                  <Check className="h-5 w-5 mr-2" />
                  Confirmar cita
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
